import { useEffect, useState } from 'react';

const ATTRIBUTION_STORAGE_KEY = 'educ4te.campaign-attribution';

/** The only query params we persist. Anything else in the URL is ignored. */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const EMPTY = Object.freeze({
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  utm_term: null,
  referrer: null,
  landingPath: null,
});

function clean(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().replace(/[<>]/g, '').slice(0, 200);
  return trimmed.length ? trimmed : null;
}

function readStored() {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * useCampaignAttribution — captures the five utm_* values plus the landing
 * context on first load and keeps them for the rest of the session.
 *
 * Why sessionStorage: the funnel is ad → landing page → course page → form.
 * The utm_* params only exist on the FIRST URL; by the time the visitor
 * reaches the registration form they are long gone from location.search. Any
 * attribution read at submit time would be empty, so every registration would
 * look organic and the Meta spend would be unmeasurable.
 *
 * Mirrors the sessionStorage caching in useResourceRequest.js — same lifetime
 * (one browser tab session), no cookie, no cross-site identifier, and nothing
 * that requires a consent banner or a CSP change.
 *
 * FIRST TOUCH WINS: once a value is stored it is never overwritten by a later
 * navigation. A visitor who arrives from an Instagram ad and later clicks an
 * organic link stays attributed to the ad that actually acquired them.
 *
 * @returns {{utm_source: string|null, utm_medium: string|null, utm_campaign: string|null,
 *            utm_content: string|null, utm_term: string|null,
 *            referrer: string|null, landingPath: string|null}}
 */
export function useCampaignAttribution() {
  const [attribution, setAttribution] = useState(EMPTY);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = readStored();
    const params = new URLSearchParams(window.location.search);

    const fromUrl = {};
    let sawUtmInUrl = false;
    for (const key of UTM_KEYS) {
      const value = clean(params.get(key));
      fromUrl[key] = value;
      if (value) sawUtmInUrl = true;
    }

    // Nothing new in the URL — keep whatever the session already holds.
    if (!sawUtmInUrl && stored) {
      setAttribution({ ...EMPTY, ...stored });
      return;
    }

    const next = { ...EMPTY, ...(stored || {}) };
    for (const key of UTM_KEYS) {
      // First touch wins: only fill a slot that is still empty.
      if (!next[key] && fromUrl[key]) next[key] = fromUrl[key];
    }
    if (!next.referrer) next.referrer = clean(document.referrer);
    if (!next.landingPath) {
      next.landingPath = clean(`${window.location.pathname}${window.location.search}`.slice(0, 500));
    }

    try {
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Private browsing / storage disabled — attribution degrades to
      // "current page only", which is still better than nothing.
    }
    setAttribution(next);
  }, []);

  return attribution;
}

export default useCampaignAttribution;
